import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'
import { activeDaysOf, addDays, firstOfMonth, formatDayFull, lastOfMonth, kindChipsOf, spanDays, statCardsOf, trendOf, type ChoiceChip, type DayRange, type LoadPart, type ReceiptsDigest } from '@krp/ui-kit'

import type { OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import { formatDateTime, formatMoney, formatTime } from '../../pricing/model/format'
import { DAY_LENGTH, MS_PER_DAY } from './constants'
import { nodeExists, registerKeysOf, selectionText, storeChoicesOf, treeNodesOf } from './tree'
import {
  EMPTY_RECEIPTS_VIEW,
  type CashReceipt,
  type CashReceiptKind,
  type CashReceiptRowVm,
  type CashReceipts,
  type CashReceiptsArgs,
  type CashReceiptsIntentState,
  type CashReceiptsNavVm,
  type CashReceiptsScreenVm,
  type CashReceiptsViewState,
  type CashRegisters,
  type CashBounds,
  type CashBoundsArgs,
  type CashSummary,
  type CashSummaryArgs,
  type CashSummaryRow,
} from './types'

/**
 * Самый длинный период экрана (около десяти лет, «за всё время»). Служба читает 1С кусками по месяцу и отдаёт только
 * статистику; список чеков запрашивается за один день, а не за период.
 */
export const CASH_RECEIPTS_MAX_DAYS = 3700

/** Дней назад, с которых начинается «За всё время»: глубже служба не читает. */
const ALL_TIME_DAYS = 3650

/** Начало «За всё время» для сегодняшнего дня: первое число месяца, с которого ещё помещается в предел службы. */
export const allTimeFromOf = (today: string): string => firstOfMonth(addDays(today, -ALL_TIME_DAYS))

export type CashRegistersResult = QueryResultLike<OneCEnvelope<CashRegisters>>
export type CashReceiptsResult = QueryResultLike<OneCEnvelope<CashReceipts>>
export type CashSummaryResult = QueryResultLike<OneCEnvelope<CashSummary>>
export type CashBoundsResult = QueryResultLike<OneCEnvelope<CashBounds>>

const NO_ROWS: readonly CashReceiptRowVm[] = []
const NO_PARTS: readonly LoadPart[] = []
const IDLE: QueryView = { status: 'idle', refreshing: false, error: null }


/** Отбор базы; период по умолчанию — сегодняшний день человека (`today` подаёт binding). */
export function cashReceiptsViewOf(state: CashReceiptsIntentState, resourceId: string): CashReceiptsViewState {
  return state[normalizeRid(resourceId)] ?? EMPTY_RECEIPTS_VIEW
}

const DAY = /^\d{4}-\d{2}-\d{2}$/

const dayNumber = (day: string): number => Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10))) / MS_PER_DAY

/** Почему период нельзя спросить у службы; `null` — можно. */
export function periodError(from: string, to: string): string | null {
  if (!DAY.test(from) || !DAY.test(to)) return 'Укажите начало и конец периода'
  if (dayNumber(to) < dayNumber(from)) return 'Конец периода раньше начала'
  if (dayNumber(to) - dayNumber(from) + 1 > CASH_RECEIPTS_MAX_DAYS) return `Период больше ${CASH_RECEIPTS_MAX_DAYS} дней — сократите его`
  return null
}

/**
 * Кассы для запроса: чеки читаются по ВСЕЙ базе за период, а место дерева отбирает экран. Так выбор места не ждёт нового
 * запроса к 1С и дерево знает, сколько чеков у каждого места. Служба умеет и отбор по кассам (`registerKey`), но экран им не пользуется.
 */
export function registerKeysForNode(_registers: CashRegisters | undefined, _nodeId: string): readonly string[] | null {
  return []
}

/** Период из одного дня — показывается список чеков; из нескольких — только статистика. */
const isSingleDay = (from: string, to: string): boolean => from === to

/** Метка «Обновить» в аргументах запросов: другой ключ кэша и приказ службе читать 1С заново. */
const freshOf = (view: CashReceiptsViewState): { fresh?: number } => (view.refreshStamp > 0 ? { fresh: view.refreshStamp } : {})

/**
 * Аргументы списка чеков или `null`: период не по правилам, кассы узла ещё неизвестны или период из нескольких дней (тогда чеки не
 * выгружаются — читается только статистика). Список чеков спрашивается за один день.
 */
export function cashReceiptsArgsOf(
  resourceId: string,
  view: CashReceiptsViewState,
  today: string,
  registers: CashRegisters | undefined,
): CashReceiptsArgs | null {
  const from = view.from ?? today
  const to = view.to ?? today
  const registerKeys = registerKeysForNode(registers, view.nodeId)
  if (resourceId === '' || registerKeys === null || periodError(from, to) !== null || !isSingleDay(from, to)) return null
  return { resourceId, from, to, registerKeys, ...freshOf(view) }
}

/**
 * Аргументы статистики или `null`: период из одного дня (его чеки уже в списке) либо не по правилам. `first` — первый день с чеками
 * в базе: месяцы до него не читаются (чеков там нет), а «за всё время» не гоняет годы пустых месяцев.
 */
export function cashSummaryArgsOf(resourceId: string, view: CashReceiptsViewState, today: string, first: string | null = null): CashSummaryArgs | null {
  const from = view.from ?? today
  const to = view.to ?? today
  if (resourceId === '' || periodError(from, to) !== null || isSingleDay(from, to)) return null
  const start = first !== null && first > from ? first : from
  // Весь период раньше первого чека базы — читать нечего.
  if (start > to) return null
  return { resourceId, from: start, to, ...freshOf(view) }
}

/** Аргументы границ базы (первый и последний чек). */
export function cashBoundsArgsOf(resourceId: string, view: CashReceiptsViewState): CashBoundsArgs | null {
  return resourceId === '' ? null : { resourceId, ...freshOf(view) }
}

const idOf = (receipt: Pick<CashReceipt, 'kind' | 'key'>) => `${receipt.kind}:${receipt.key}`

function rowOf(receipt: CashReceipt): CashReceiptRowVm {
  // Отложенный (или не проведённый) чек — не продажа: помечен «Отложен», оплаты у него нет, в итоги он не входит.
  const counts = receipt.countsInSales
  return {
    id: idOf(receipt),
    kind: receipt.kind,
    receiptKey: receipt.key,
    refund: receipt.kind === 'refund',
    number: receipt.receiptNumber ?? receipt.number ?? '',
    at: formatDateTime(receipt.date),
    register: receipt.registerName ?? (receipt.registerKey === null ? 'касса не указана' : 'касса не найдена в справочнике'),
    cashier: receipt.cashierName ?? '',
    shift: receipt.shiftNumber ?? '',
    status: counts ? [receipt.status, 'проведён'].filter((part) => part !== null).join(' · ') : 'Отложен',
    total: receipt.amount,
    card: counts ? receipt.cardAmount : null,
    cash: !counts || receipt.amount === null || receipt.cardAmount === null ? null : Math.round((receipt.amount - receipt.cardAmount) * 100) / 100,
    muted: !counts,
  }
}

/** День чека: дата 1С — местное время без пояса, первые десять знаков. */
const dayOf = (item: CashReceipt): string => (item.date ?? '').slice(0, DAY_LENGTH)

/** Срезы статистики и имена кассиров: из ответа статистики или из чеков одного дня — дальше экран не различает откуда. */
interface Cube {
  rows: readonly CashSummaryRow[]
  names: ReadonlyMap<string, string>
  truncated: boolean
  observedAt: string
}

const NO_CUBE_ROWS: readonly CashSummaryRow[] = []

function cubeOfSummary(summary: CashSummary): Cube {
  return { rows: summary.rows, names: new Map(summary.cashiers.map((item) => [item.key, item.name])), truncated: summary.truncated, observedAt: summary.observedAt }
}

/** Чеки одного дня сворачиваются в те же срезы, что отдаёт служба для периода: одна сводка на оба режима. */
function cubeOfReceipts(receipts: CashReceipts): Cube {
  const cells = new Map<string, CashSummaryRow>()
  const names = new Map<string, string>()
  for (const item of receipts.items) {
    if (!item.countsInSales) continue
    const day = dayOf(item)
    if (!DAY.test(day)) continue
    if (item.cashierKey !== null && item.cashierName !== null) names.set(item.cashierKey, item.cashierName)
    const id = [day, item.registerKey ?? '', item.cashierKey ?? '', item.kind].join('|')
    const cell = cells.get(id) ?? { day, registerKey: item.registerKey, cashierKey: item.cashierKey, kind: item.kind, count: 0, amount: 0, cardAmount: 0 }
    cells.set(id, { ...cell, count: cell.count + 1, amount: cell.amount + (item.amount ?? 0), cardAmount: cell.cardAmount + (item.cardAmount ?? 0) })
  }

  return { rows: [...cells.values()], names, truncated: receipts.truncated, observedAt: receipts.observedAt }
}

/** Срезы (рубли) → общая сводка чеков. Карточки, чипы вида, дни и график строит общий слой ui-kit — тот же, что у AQSI. */
function digestOf(rows: readonly CashSummaryRow[]): ReceiptsDigest {
  const sum = (kind: CashReceiptKind, pick: (row: CashSummaryRow) => number) => rows.filter((row) => row.kind === kind).reduce((total, row) => total + pick(row), 0)
  const byDay = new Map<string, { saleCount: number; saleSum: number; refundCount: number; refundSum: number }>()
  for (const row of rows) {
    const entry = byDay.get(row.day) ?? { saleCount: 0, saleSum: 0, refundCount: 0, refundSum: 0 }
    if (row.kind === 'sale') {
      entry.saleCount += row.count
      entry.saleSum += row.amount
    } else {
      entry.refundCount += row.count
      entry.refundSum += row.amount
    }
    byDay.set(row.day, entry)
  }
  const saleSum = sum('sale', (row) => row.amount)
  const saleCard = sum('sale', (row) => row.cardAmount)
  const refundSum = sum('refund', (row) => row.amount)
  const refundCard = sum('refund', (row) => row.cardAmount)
  return {
    sales: { count: sum('sale', (row) => row.count), sum: saleSum, detail: `картой ${formatMoney(saleCard)}`, cash: saleSum - saleCard, cashless: saleCard },
    refunds: { count: sum('refund', (row) => row.count), sum: refundSum, detail: null, cash: refundSum - refundCard, cashless: refundCard },
    days: [...byDay.entries()].map(([day, entry]) => ({ day, ...entry })),
    cashiers: [],
  }
}

/** Селект «Кассир»: все кассиры места и периода с числом чеков; выбранный, которого уже нет в отборе, остаётся — иначе его не сбросить. */
function cashierChipsOf(rows: readonly CashSummaryRow[], names: ReadonlyMap<string, string>, selected: string): readonly ChoiceChip[] {
  const counts = new Map<string, number>()
  for (const row of rows) if (row.cashierKey !== null) counts.set(row.cashierKey, (counts.get(row.cashierKey) ?? 0) + row.count)
  const nameOf = (key: string) => names.get(key) ?? 'Кассир без имени в справочнике'
  const chips: ChoiceChip[] = [{ id: '', label: 'Все кассиры' }]
  for (const [key, count] of [...counts.entries()].sort((left, right) => nameOf(left[0]).localeCompare(nameOf(right[0]), 'ru')))
    chips.push({ id: key, label: nameOf(key), count })
  if (selected !== '' && !counts.has(selected)) chips.push({ id: selected, label: nameOf(selected) })
  return chips
}

function countByRegister(rows: readonly CashSummaryRow[]): ReadonlyMap<string, number> {
  const counts = new Map<string, number>()
  for (const row of rows) if (row.registerKey !== null) counts.set(row.registerKey, (counts.get(row.registerKey) ?? 0) + row.count)
  return counts
}

/** Чек подходит под строку поиска: номер, касса, статус, сумма (как её видит человек: «200», «200,00»). */
function matchesSearch(item: CashReceipt, needle: string): boolean {
  const text = needle.trim().toLowerCase()
  if (text === '') return true
  const amount = item.amount === null ? [] : [String(item.amount), String(item.amount).replace('.', ','), formatMoney(item.amount).toLowerCase()]
  return [item.number, item.receiptNumber, item.registerName, item.status, ...amount].some((part) => part !== null && part.toLowerCase().includes(text))
}

export interface CashReceiptsScreenInput {
  resourceId: string
  today: string
  intent: CashReceiptsIntentState
  registers: CashRegistersResult
  /** Чеки одного дня (период из нескольких дней чеков не читает). */
  receipts: CashReceiptsResult | null
  /** Статистика периода из нескольких дней. */
  summary?: CashSummaryResult | null
  monthSummary?: CashSummaryResult | null
  /** Первый и последний день с чеками в базе. */
  bounds?: CashBoundsResult | null
  /** Состояние каждого месяца при чтении периода по месяцам. */
  summaryParts?: readonly LoadPart[]
  /** Сколько месяцев периода прочитано, пока идёт чтение; `null` — прочитано всё. */
  summaryProgress?: { done: number; total: number } | null
  /** Перечитывается сегодняшний день (прошлые — из кэша). */
  todayRefreshing?: boolean
}

export const makeSelectCashReceiptsScreenVm = () => {
  /** Срезы периода или дня — откуда они, зависит от режима; чьё чтение завершилось, того и показываем. */
  const selectCube = createSelector(
    [(input: CashReceiptsScreenInput) => input.receipts?.data?.data, (input: CashReceiptsScreenInput) => input.summary?.data?.data,
      (input: CashReceiptsScreenInput) => cashReceiptsViewOf(input.intent, input.resourceId), (input: CashReceiptsScreenInput) => input.today],
    (listed, summarized, view, today): Cube | undefined => (summarized !== undefined
      ? cubeOfSummary({ ...summarized, rows: summarized.rows.filter((row) => row.day >= (view.from ?? today) && row.day <= (view.to ?? today)) })
      : listed !== undefined ? cubeOfReceipts(listed) : undefined),
  )
  const selectRegistersView = createSelector(
    [
      (input: CashReceiptsScreenInput) => input.registers.isUninitialized,
      (input: CashReceiptsScreenInput) => input.registers.isLoading,
      (input: CashReceiptsScreenInput) => input.registers.isError,
      (input: CashReceiptsScreenInput) => input.registers.isSuccess,
      (input: CashReceiptsScreenInput) => input.registers.error,
      (input: CashReceiptsScreenInput) => input.registers.data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        { isUninitialized, isLoading, isError, isSuccess, ...(error !== undefined ? { error } : {}), ...(data !== undefined ? { data } : {}) },
        (value) => value.data.registers.length === 0,
      ),
  )

  const selectNav = createSelector(
    [
      selectRegistersView,
      (input: CashReceiptsScreenInput) => input.registers.data?.data,
      (input: CashReceiptsScreenInput) => cashReceiptsViewOf(input.intent, input.resourceId),
      selectCube,
    ],
    (query, directory, view, cube): CashReceiptsNavVm => {
      // Чеков по кассам за период — вся база: дерево показывает, где чеки есть, и выбор места не ждёт нового запроса.
      const counts = cube === undefined ? null : countByRegister(cube.rows.filter((row) => view.cashier === '' || row.cashierKey === view.cashier))
      // Выбранный узел, которого в базе больше нет, — вся база: отбор не должен молча стать пустым.
      const selectedId = nodeExists(directory, view.nodeId) ? view.nodeId : ''
      return {
        query,
        nodes: treeNodesOf(directory, view.collapsed, selectedId, counts),
        stores: storeChoicesOf(directory, counts),
        selectedId,
        summaryText: selectionText(directory, selectedId),
        note:
          query.error === null
            ? null
            : `Кассы не прочитаны: ${query.error.message}. Чеки показываются по всей базе, выбрать место нельзя.`,
      }
    },
  )

  return createSelector(
    [
      selectNav,
      (input: CashReceiptsScreenInput) => cashReceiptsViewOf(input.intent, input.resourceId),
      (input: CashReceiptsScreenInput) => input.today,
      (input: CashReceiptsScreenInput) => input.registers.data?.data,
      (input: CashReceiptsScreenInput) => input.receipts,
      (input: CashReceiptsScreenInput) => input.summary,
      (input: CashReceiptsScreenInput) => input.receipts?.data?.data,
      selectCube,
      (input: CashReceiptsScreenInput) => input.summaryProgress ?? null,
      (input: CashReceiptsScreenInput) => input.monthSummary,
      (input: CashReceiptsScreenInput) => input.bounds,
      (input: CashReceiptsScreenInput) => input.summaryParts ?? NO_PARTS,
      (input: CashReceiptsScreenInput) => input.todayRefreshing === true,
    ],
    (nav, view, today, directory, receipts, summary, listed, cube, progress, monthly, bounds, parts, todayBusy): CashReceiptsScreenVm => {
      const from = view.from ?? today
      const to = view.to ?? today
      const period: DayRange = { from, to }
      const mode = isSingleDay(from, to) ? 'day' : 'range'
      // Срезы места: служба отдаёт всю базу, место дерева отбирает экран.
      const keys = nav.selectedId === '' ? null : new Set(registerKeysOf(directory, nav.selectedId))
      const placeRows = (cube?.rows ?? NO_CUBE_ROWS).filter((row) => keys === null || (row.registerKey !== null && keys.has(row.registerKey)))
      const cashierRows = view.cashier === '' ? placeRows : placeRows.filter((row) => row.cashierKey === view.cashier)
      const names = cube?.names ?? new Map<string, string>()
      const digest = cube === undefined ? undefined : digestOf(cashierRows)
      // Чеки дня: место, кассир, вид и поиск — на клиенте, чеки дня уже на экране.
      const inPlace = (listed?.items ?? []).filter(
        (item) => (keys === null || (item.registerKey !== null && keys.has(item.registerKey))) && (view.cashier === '' || item.cashierKey === view.cashier),
      )
      const shown = inPlace.filter((item) => (view.kind === '' || item.kind === view.kind) && matchesSearch(item, view.search))
      const query =
        mode === 'day'
          ? receipts === null
            ? IDLE
            : queryView(receipts, (data) => data.data.items.length === 0)
          : summary == null
            ? IDLE
            : queryView(summary, (data) => data.data.rows.length === 0)
      const hasCashiers = placeRows.some((row) => row.cashierKey !== null)
      const boundsData = bounds?.isSuccess === true ? bounds.data?.data : undefined
      const firstDay = boundsData?.first ?? null
      const boundsNote = boundsData === undefined
        ? null
        : firstDay === null
          ? 'В базе 1С пока нет чеков.'
          : from < firstDay
            ? `В базе 1С чеки есть с ${formatDayFull(firstDay)}: раньше чеков нет, поэтому итоги и график начинаются с этого дня.`
            : null
      return {
        today,
        period,
        nav,
        mode,
        trend: monthly?.data?.data !== undefined && monthly.isSuccess
          ? trendOf(digestOf(monthly.data.data.rows.filter((row) =>
            (keys === null || row.registerKey !== null && keys.has(row.registerKey)) && (view.cashier === '' || row.cashierKey === view.cashier))).days,
          firstOfMonth(from), lastOfMonth(from), formatMoney)
          : monthly === undefined && mode === 'range' && digest !== undefined ? trendOf(digest.days, from, to, formatMoney) : [],
        // График длинного периода начинается с первого чека базы: годы до него — не «пустые месяцы», а отсутствие данных.
        periodTrend: mode === 'range' && digest !== undefined ? trendOf(digest.days, firstDay !== null && firstDay > from ? firstDay : from, to, formatMoney) : [],
        trendQuery: monthly == null ? IDLE : queryView(monthly, () => false),
        allTimeFrom: firstDay !== null && firstOfMonth(firstDay) > allTimeFromOf(today) ? firstOfMonth(firstDay) : allTimeFromOf(today),
        firstDay,
        boundsNote,
        todayBusy,
        refreshing: todayBusy || query.refreshing || query.status === 'loading' || progress !== null || (monthly != null && queryView(monthly, () => false).refreshing),
        progressNote: mode === 'range' && progress !== null ? `Читаю 1С по месяцам: ${progress.done} из ${progress.total}. Итоги пока неполные.` : null,
        statsBusy: (summary == null ? receipts?.isLoading === true : summary.isLoading) || progress !== null || bounds?.isLoading === true,
        loadParts: parts,
        stats: statCardsOf(digest, { money: formatMoney, periodDays: periodError(from, to) === null ? spanDays(from, to) : 0, truncated: cube?.truncated === true }),
        days: activeDaysOf(digest, formatMoney),
        kinds: kindChipsOf(digest),
        cashiers: hasCashiers || view.cashier !== '' ? cashierChipsOf(placeRows, names, view.cashier) : [{ id: '', label: 'Все кассиры' }],
        cashier: view.cashier,
        kind: view.kind,
        search: view.search,
        periodError: periodError(from, to),
        query,
        readAt: cube === undefined ? null : `Прочитано из 1С в ${formatTime(cube.observedAt)}`,
        truncatedNote: cube?.truncated === true ? 'Чеков за период больше, чем прочитано: итоги неполные. Сократите период или выберите место поуже.' : null,
        // За период чеки в базе есть, а в выбранном месте (или по виду и поиску) — нет: экран объясняет, куда смотреть.
        filteredOut: mode === 'day' && (listed?.items.length ?? 0) > 0 && shown.length === 0,
        rows: shown.length === 0 ? NO_ROWS : shown.map(rowOf),
      }
    },
  )
}
